// HACKATHON: Calendar Components - Earnings + Economic
import React, { useState, useEffect } from 'react';
import { getFlagByTicker } from '../utils/CountryMapper';

// ============================================
// HOOKS
// ============================================
//const API_BASE = 'http://100.119.212.17:5020/api';  // Earnings on 8091
const API_BASE_ECON = '/api/economic/api';  // Economic on 8092
const API_BASE = '/api/earnings/api';

export const useEarningsCalendar = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE}/earnings_week`);
        const json = await res.json();
        setData(json);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching earnings:', err);
        setLoading(false);
      }
    };

    fetchData();
    const timer = setInterval(fetchData, 300000); // 5 min refresh
    return () => clearInterval(timer);
  }, []);

  return { data, loading };
};

export const useEconomicCalendar = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE_ECON}/economic_events`);
        const json = await res.json();
        setData(json);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching economic events:', err);
        setLoading(false);
      }
    };

    fetchData();
    const timer = setInterval(fetchData, 300000); // 5 min refresh
    return () => clearInterval(timer);
  }, []);

  return { data, loading };
};

// ============================================
// EARNINGS CALENDAR CARD
// ============================================

export const EarningsCalendarCard = ({ theme, maxDays = 7 }) => {
  const { data: earningsData, loading } = useEarningsCalendar();

  if (loading) {
    return (
      <div style={{
        background: theme.cardBg,
        borderRadius: '12px',
        padding: '1.5rem',
        border: `1px solid ${theme.border}`,
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37' }}>
          📊 Earnings Calendar
        </h3>
        <p style={{ color: theme.textSecondary, marginTop: '1rem' }}>
          Carregando...
        </p>
      </div>
    );
  }

  if (!earningsData || earningsData.length === 0) {
    return (
      <div style={{
        background: theme.cardBg,
        borderRadius: '12px',
        padding: '1.5rem',
        border: `1px solid ${theme.border}`,
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37' }}>
          📊 Earnings Calendar
        </h3>
        <p style={{ color: theme.textSecondary, marginTop: '1rem' }}>
          Nenhum evento
        </p>
      </div>
    );
  }

  const displayDays = earningsData.slice(0, maxDays);

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '12px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)'
    }}>
      <h3 style={{ 
        fontSize: '1.1rem', 
        fontWeight: 'bold', 
        color: '#D4AF37',
        marginBottom: '1.5rem'
      }}>
        📊 Earnings Calendar (Next {maxDays} Days)
      </h3>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {displayDays.map((day, dayIdx) => (
          <EarningsDaySection key={dayIdx} day={day} theme={theme} />
        ))}
      </div>
    </div>
  );
};

// Individual day section
const EarningsDaySection = ({ day, theme }) => {
  const [expanded, setExpanded] = useState(true);
  
  const totalEvents = 
    day.groups['Pre Market'].length + 
    day.groups['During Market'].length + 
    day.groups['Post Market'].length;

  return (
    <div style={{
      background: 'rgba(0,0,0,0.2)',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Day Header */}
      <div 
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '0.75rem 1rem',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: expanded ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
          transition: 'all 0.3s'
        }}
      >
        <div>
          <span style={{ fontWeight: 'bold', color: theme.textPrimary, fontSize: '0.9rem' }}>
            {day.day}
          </span>
          <span style={{ fontSize: '0.75rem', color: theme.textSecondary, marginLeft: '0.5rem' }}>
            ({totalEvents} eventos)
          </span>
        </div>
        <span style={{ color: '#D4AF37', fontSize: '0.9rem' }}>
          {expanded ? '▼' : '▶'}
        </span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '1rem' }}>
          {day.groups['Pre Market'].length > 0 && (
            <TimeGroupSection 
              title="Pre Market" 
              events={day.groups['Pre Market']} 
              theme={theme} 
            />
          )}
          {day.groups['During Market'].length > 0 && (
            <TimeGroupSection 
              title="During Market" 
              events={day.groups['During Market']} 
              theme={theme} 
            />
          )}
          {day.groups['Post Market'].length > 0 && (
            <TimeGroupSection 
              title="Post Market" 
              events={day.groups['Post Market']} 
              theme={theme} 
            />
          )}
        </div>
      )}
    </div>
  );
};

// Time group (Pre/During/Post Market)
const TimeGroupSection = ({ title, events, theme }) => {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ 
        fontSize: '0.75rem', 
        color: '#D4AF37', 
        fontWeight: 'bold',
        marginBottom: '0.5rem',
        textTransform: 'uppercase'
      }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {events.map((event, idx) => (
          <div 
            key={idx}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.5rem',
              background: 'rgba(0,0,0,0.15)',
              borderRadius: '6px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1rem' }}>
                {getFlagByTicker(event.Ticker)}
              </span>
              <div>
                <span style={{ fontWeight: 600, color: theme.textPrimary, fontSize: '0.85rem' }}>
                  {event.Ticker}
                </span>
                <span style={{ fontSize: '0.7rem', color: theme.textSecondary, marginLeft: '0.5rem' }}>
                  {event.longName}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// ECONOMIC CALENDAR CARD
// ============================================

export const EconomicCalendarCard = ({ theme, maxEvents = 15 }) => {
  const { data: economicData, loading } = useEconomicCalendar();

  if (loading) {
    return (
      <div style={{
        background: theme.cardBg,
        borderRadius: '12px',
        padding: '1.5rem',
        border: `1px solid ${theme.border}`,
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37' }}>
          📅 Economic Calendar
        </h3>
        <p style={{ color: theme.textSecondary, marginTop: '1rem' }}>
          Carregando...
        </p>
      </div>
    );
  }

  if (!economicData || economicData.length === 0) {
    return (
      <div style={{
        background: theme.cardBg,
        borderRadius: '12px',
        padding: '1.5rem',
        border: `1px solid ${theme.border}`,
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37' }}>
          📅 Economic Calendar
        </h3>
        <p style={{ color: theme.textSecondary, marginTop: '1rem' }}>
          Nenhum evento
        </p>
      </div>
    );
  }

  const displayEvents = economicData.slice(0, maxEvents);

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '12px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)'
    }}>
      <h3 style={{ 
        fontSize: '1.1rem', 
        fontWeight: 'bold', 
        color: '#D4AF37',
        marginBottom: '1.5rem'
      }}>
        📅 Economic Calendar (This Week)
      </h3>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {displayEvents.map((event, idx) => (
          <EconomicEventRow key={idx} event={event} theme={theme} />
        ))}
      </div>
    </div>
  );
};

// Individual economic event
const EconomicEventRow = ({ event, theme }) => {
  const impactColorMap = {
    'danger': '#ef4444',
    'warning': '#f59e0b',
    'secondary': '#6b7280',
    'info': '#3b82f6'
  };

  const impactColor = impactColorMap[event.impact_color] || '#6b7280';

  return (
    <div style={{
      display: 'flex',
      gap: '0.75rem',
      padding: '0.75rem',
      background: 'rgba(0,0,0,0.2)',
      borderRadius: '8px',
      borderLeft: `3px solid ${impactColor}`
    }}>
      {/* Flag + Time */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        minWidth: '60px'
      }}>
        <span style={{ fontSize: '1.2rem' }}>{event.country_flag}</span>
        <span style={{ fontSize: '0.7rem', color: theme.textSecondary }}>
          {event.time}
        </span>
      </div>

      {/* Event Details */}
      <div style={{ flex: 1 }}>
        <div style={{ 
          fontWeight: 600, 
          color: theme.textPrimary, 
          fontSize: '0.85rem',
          marginBottom: '0.25rem'
        }}>
          {event.title}
        </div>
        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.7rem', color: theme.textSecondary }}>
          <span>{event.day_name}</span>
          {event.forecast && <span>Forecast: {event.forecast}</span>}
          {event.previous && <span>Prev: {event.previous}</span>}
        </div>
      </div>

      {/* Impact Badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center'
      }}>
        <span style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '12px',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          background: `${impactColor}20`,
          color: impactColor,
          border: `1px solid ${impactColor}`
        }}>
          {event.impact}
        </span>
      </div>
    </div>
  );
};